import Link from 'next/link';
import { SITE_URL } from '../../utils/schema';

/**
 * The trail carries absolute URLs because the JSON-LD needs them, but the
 * visible links should stay in-site — an absolute href would send a visitor
 * on a preview deployment straight to production.
 */
function toInternalHref(url) {
  if (!url) return url;
  return url.startsWith(SITE_URL) ? url.slice(SITE_URL.length) || '/' : url;
}

/**
 * Visible breadcrumb trail. The matching BreadcrumbList JSON-LD is emitted
 * separately by each page (utils/schema.js) — Google expects the markup to
 * reflect a trail the user can actually see, which is why this exists rather
 * than schema alone.
 *
 * `trail` is the same array passed to breadcrumbSchema: the last entry is the
 * current page and is rendered as plain text, not a link.
 */
function Breadcrumbs({ trail = [], className = '' }) {
  if (!trail.length) return null;

  return (
    <nav aria-label='Breadcrumb' className={`breadcrumbs ${className}`.trim()}>
      <ol className='breadcrumbsList'>
        {trail.map((crumb, index) => {
          const isLast = index === trail.length - 1;
          return (
            <li key={`${crumb.name}-${index}`} className='breadcrumbsItem'>
              {isLast || !crumb.url ? (
                <span aria-current='page' className='breadcrumbsCurrent'>
                  {crumb.name}
                </span>
              ) : (
                <>
                  <Link href={toInternalHref(crumb.url)}>
                    <a className='breadcrumbsLink'>{crumb.name}</a>
                  </Link>
                  <span aria-hidden='true' className='breadcrumbsSeparator'>
                    /
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
